import api from "services/api";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";
import { createNewEmployee } from "store/features/company/employee/employeeActions";
import addEmployeeSvg from "assets/modals/add.svg";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { fetchDesignations } from "store/features/company/userManagement/designation/designationActions";
import { selectDesignations } from "store/features/company/userManagement/designation/designationSelectors";

const AddEmployeeModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [employeeCodeError, setEmployeeCodeError] = useState("");
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    employee_code: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    gender: "male",
    date_of_birth: "",
    joining_date: "",
    department_id: "",
    designation_id: "",
    employment_type: "permanent",
    work_location: "",
    basic_salary: "",
    ctc: "",
  });

  const departments = useSelector(selectDepartments);
  const designations = useSelector(selectDesignations);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDepartments());
      dispatch(fetchDesignations());
    }
  }, [isOpen, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "employee_code") {
      checkEmployeeCode(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (employeeCodeError) {
      showErrorToast("Please use a different employee code");
      return;
    }
    setLoading(true);
    const formattedData = {
      ...formData,
      department_id: formData.department_id
        ? Number(formData.department_id)
        : null,
      basic_salary: formData.basic_salary ? Number(formData.basic_salary) : 0,
      ctc: formData.ctc ? Number(formData.ctc) : 0,
    };

    const result = await dispatch(createNewEmployee(formattedData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      employee_code: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      gender: "male",
      date_of_birth: "",
      joining_date: "",
      department_id: "",
      employment_type: "permanent",
      work_location: "",
      basic_salary: "",
      ctc: "",
    });
  };
  const checkEmployeeCode = async (code) => {
    if (!code || code.length < 3) {
      setEmployeeCodeError("");
      return;
    }

    setIsCheckingCode(true);
    try {
      const response = await api.get("v1/api/employees/list");
      if (response?.meta?.status === true) {
        const existingEmployee = response.data.find(
          (emp) => emp.employee_code === code,
        );
        if (existingEmployee) {
          setEmployeeCodeError("This employee code is already taken");
        } else {
          setEmployeeCodeError("");
        }
      }
    } catch (error) {
      console.error("Error checking employee code:", error);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      "&.Mui-focused": {
        color: "#f59e0b",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>
          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                position: "relative",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addEmployeeSvg}
                      alt="Add Employee"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Add Employee
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Add a new team member to your organization
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Name Fields Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter first name"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      placeholder="Enter last name"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Employee Code */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Employee Code"
                      name="employee_code"
                      value={formData.employee_code}
                      onChange={handleChange}
                      required
                      placeholder="e.g., EMP001"
                      error={!!employeeCodeError}
                      helperText={
                        employeeCodeError ||
                        "Unique employee code (e.g., EMP001, EMP002)"
                      }
                      InputProps={{
                        endAdornment: isCheckingCode && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor: employeeCodeError
                              ? "#ef4444"
                              : theme.palette.mode === "dark"
                              ? "#475569"
                              : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: employeeCodeError
                              ? "#ef4444"
                              : "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: employeeCodeError
                              ? "#ef4444"
                              : "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: employeeCodeError
                            ? "#ef4444"
                            : theme.palette.mode === "dark"
                            ? "#9ca3af"
                            : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: employeeCodeError ? "#ef4444" : "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Stack>

                  {/* Email and Phone Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="employee@company.com"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="9876543210"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Employment Type and Work location Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Employment Type"
                      name="employment_type"
                      value={formData.employment_type}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="permanent">Permanent</MenuItem>
                      <MenuItem value="contract">Contract</MenuItem>
                      <MenuItem value="intern">Intern</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      label="Work Location"
                      name="work_location"
                      value={formData.work_location}
                      onChange={handleChange}
                      placeholder="Delhi, Mumbai, etc."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Date Fields Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      name="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      required
                      placeholder="DD/MM/YYYY"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Joining Date"
                      name="joining_date"
                      type="date"
                      value={formData.joining_date}
                      onChange={handleChange}
                      required
                      placeholder="DD/MM/YYYY"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                        "& .MuiInputLabel-asterisk": {
                          color: "#ef4444",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Department and Work Location Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Department"
                      name="department_id"
                      value={formData.department_id}
                      onChange={handleChange}
                      placeholder="Select department"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="">Select Department</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name} ({dept.code})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      select
                      label="Designation"
                      name="designation_id"
                      value={formData.designation_id}
                      onChange={handleChange}
                      placeholder="Select designation"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem value="">Select Designation</MenuItem>
                      {designations.map((desg) => (
                        <MenuItem key={desg.id} value={desg.id}>
                          {desg.name} ({desg.code})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  {/* Salary Fields Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Basic Salary (₹)"
                      name="basic_salary"
                      type="number"
                      value={formData.basic_salary}
                      onChange={handleChange}
                      placeholder="25000"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="CTC (₹)"
                      name="ctc"
                      type="number"
                      value={formData.ctc}
                      onChange={handleChange}
                      placeholder="42000"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1e293b"
                              : "#f9fafb",
                          "& fieldset": {
                            borderColor:
                              theme.palette.mode === "dark"
                                ? "#475569"
                                : "#d1d5db",
                          },
                          "&:hover fieldset": {
                            borderColor: "#f59e0b",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#f59e0b",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                          position: "relative",
                          transform: "none",
                          marginBottom: "8px",
                          display: "block",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#f59e0b",
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Add Employee"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddEmployeeModal;
